import { supabase } from "@/integrations/supabase/client";
import {
  ImportCaseStudyRequest,
  ImportCaseStudyResponse,
  ImportedCaseStudySchema,
  ImportError,
  ImportErrorCategory,
} from "@/types/case-study-import";
import { CaseStudyItem } from "@/types/case-study";

/**
 * Categorize import errors for better user messaging
 */
function categorizeImportError(error: any): ImportError {
  const errorMessage = error?.message || String(error);

  // URL validation errors
  if (errorMessage.includes('Invalid URL') || errorMessage.includes('Valid URL is required')) {
    return {
      category: 'url_invalid',
      message: 'The URL you provided is not valid.',
      suggestion: 'Please check the URL and try again.',
    };
  }

  // Scraping errors
  if (errorMessage.includes('Failed to access') || errorMessage.includes('blocked') || errorMessage.includes('protected')) {
    return {
      category: 'url_unreachable',
      message: 'Unable to access the case study page.',
      suggestion: 'The page may be password-protected, blocked by robots.txt, or require authentication. Try a publicly accessible URL.',
    };
  }

  if (errorMessage.includes('Failed to scrape')) {
    return {
      category: 'scraping_failed',
      message: 'Could not extract content from the page.',
      suggestion: 'The page structure may be incompatible. Try a different case study URL or build from scratch.',
    };
  }

  // AI processing errors
  if (errorMessage.includes('OpenAI') || errorMessage.includes('AI processing')) {
    return {
      category: 'ai_processing_failed',
      message: 'Failed to process the case study content.',
      suggestion: 'Please try again or build your case study from scratch.',
    };
  }

  // Rate limiting
  if (errorMessage.includes('rate limit') || errorMessage.includes('too many requests')) {
    return {
      category: 'rate_limit',
      message: 'Too many import requests.',
      suggestion: 'Please wait a moment and try again.',
    };
  }

  // Validation errors - case study content validation
  if (errorMessage.includes('does not appear to contain a case study') ||
      errorMessage.includes('not a case study') ||
      errorMessage.includes('case study page')) {
    return {
      category: 'content_extraction_failed',
      message: 'This page does not appear to contain a case study.',
      suggestion: 'Please provide a URL to a project case study page (not a portfolio listing or blog post).',
    };
  }

  // Data validation errors
  if (errorMessage.includes('validation') || errorMessage.includes('invalid data')) {
    return {
      category: 'validation_failed',
      message: 'The extracted data is invalid.',
      suggestion: 'The case study structure may be unusual. Try building from scratch.',
    };
  }

  // Default unknown error
  return {
    category: 'unknown',
    message: 'An unexpected error occurred while importing.',
    details: errorMessage,
    suggestion: 'Please try again or contact support if the problem persists.',
  };
}

/**
 * Progress callback for import process
 */
export interface ImportProgressCallback {
  onProgress?: (progress: number, stage: number) => void;
}

/**
 * Import a case study from a URL
 */
export async function importCaseStudyFromUrl(
  url: string,
  callbacks?: ImportProgressCallback
): Promise<ImportCaseStudyResponse> {
  const reportProgress = (progress: number, stage: number) => {
    callbacks?.onProgress?.(progress, stage);
  };

  try {
    console.log('[ImportService] Starting case study import for:', url);
    reportProgress(1, 1); // Start at 1%, stage 1

    // Client-side URL validation
    try {
      new URL(url);
    } catch {
      const error = categorizeImportError(new Error('Invalid URL format'));
      console.error('[ImportService] URL validation failed');
      return {
        success: false,
        error: error.message,
      };
    }

    console.log('[ImportService] Calling Supabase edge function...');
    reportProgress(10, 2); // Start at 10% for stage 2

    // Use direct fetch to get full response including error bodies
    // The Supabase client doesn't expose response bodies for non-2xx responses
    const { data: { session } } = await supabase.auth.getSession();
    const functionUrl = `${supabase.supabaseUrl}/functions/v1/import-case-study-from-url`;

    console.log('[ImportService] Calling edge function with direct fetch...');

    let responseData: any = null;
    let responseError: any = null;

    try {
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({ url } as ImportCaseStudyRequest),
      });

      // Parse the JSON response body (works for both success and error responses)
      const responseBody = await response.json();
      console.log('[ImportService] Response status:', response.status);
      console.log('[ImportService] Response body:', responseBody);

      reportProgress(95, 3); // Jump to 95% when API returns

      if (!response.ok) {
        // Non-2xx response - extract error from response body
        responseError = new Error(responseBody.error || `HTTP ${response.status}`);
        responseData = responseBody;
      } else {
        // Success response
        responseData = responseBody;
      }

    } catch (fetchError) {
      console.error('[ImportService] Fetch error:', fetchError);
      responseError = fetchError;
    }

    // Handle error responses
    if (responseError) {
      console.error('[ImportService] Edge function error:', responseError);

      // Extract the actual error message from response body
      let actualErrorMessage = responseError.message || 'Unknown error';

      if (responseData && typeof responseData === 'object' && 'error' in responseData) {
        actualErrorMessage = responseData.error;
        console.log('[ImportService] Extracted error from response body:', actualErrorMessage);
      }

      const categorizedError = categorizeImportError(new Error(actualErrorMessage));
      return {
        success: false,
        error: categorizedError.message,
      };
    }

    if (!responseData || !responseData.success) {
      console.error('[ImportService] Import failed:', responseData?.error);
      const categorizedError = categorizeImportError(new Error(responseData?.error || 'Unknown error'));
      return {
        success: false,
        error: categorizedError.message,
      };
    }

    console.log('[ImportService] Edge function returned success, validating data...');
    // Let it creep slowly to 100% - no progress jump here

    // Validate the imported data
    try {
      const validatedData = ImportedCaseStudySchema.parse(responseData.data);

      console.log('[ImportService] Successfully imported case study:', {
        title: validatedData.title,
        sectionsCount: validatedData.sections?.length || 0,
      });

      reportProgress(100, 4); // Stage 4: Preparing case study (Complete)

      const caseStudyDataWithOrigin: Partial<CaseStudyItem> = {
        ...(responseData.data as Partial<CaseStudyItem>),
        creation_method: 'imported',
      };

      return {
        success: true,
        data: caseStudyDataWithOrigin,
        metadata: responseData.metadata,
        eventId: responseData.eventId,
      };
    } catch (validationError) {
      console.error('[ImportService] Validation error:', validationError);
      const categorizedError = categorizeImportError(validationError);
      return {
        success: false,
        error: categorizedError.message,
      };
    }

  } catch (error) {
    console.error('[ImportService] Unexpected error:', error);
    const categorizedError = categorizeImportError(error);
    return {
      success: false,
      error: categorizedError.message,
    };
  }
}

/**
 * Helper to get user-friendly error messages
 */
export function getImportErrorMessage(error: ImportError): string {
  const messages: Record<ImportErrorCategory, string> = {
    url_invalid: 'Please enter a valid URL to your case study page.',
    url_unreachable: 'We couldn\'t access this page. It may be password-protected or blocked.',
    scraping_failed: 'We couldn\'t extract content from this page. Try a different URL.',
    content_extraction_failed: 'The page doesn\'t appear to contain a case study.',
    ai_processing_failed: 'We had trouble processing the content. Please try again.',
    validation_failed: 'The imported data is incomplete. Try building from scratch.',
    rate_limit: 'Too many requests. Please wait a moment and try again.',
    unknown: 'Something went wrong. Please try again or build from scratch.',
  };

  return messages[error.category] || error.message;
}
